import { FaceDetector, FilesetResolver } from "@mediapipe/tasks-vision";
export const FACE_BLUR_ERROR_MESSAGE = "Kunde inte sudda ut ansikten i bilden. Försök igen eller välj en annan bild.";

/**
 * WASM runtime version served from the CDN. Must equal the installed
 * `@mediapipe/tasks-vision` version: the JS API and the WASM it loads are
 * released together (a unit test checks this).
 */
export const MEDIAPIPE_WASM_VERSION = "1.1.1";
let detector = null;
let loading = null;
async function getDetector() {
  if (detector) return detector;
  if (loading) return loading;
  loading = (async () => {
    const vision = await FilesetResolver.forVisionTasks(`https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_WASM_VERSION}/wasm`);
    detector = await FaceDetector.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_full_range/float16/1/blaze_face_full_range.tflite"
      },
      runningMode: "IMAGE",
      minDetectionConfidence: 0.3
    });
    return detector;
  })();

  // A failed load must not be cached, otherwise "Försök igen" can never work.
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

/**
 * Canvas limits. iOS WebKit refuses canvases above ~16.7 MP (`toDataURL()`
 * then yields "data:,"), and 24 MP phone photos exceed that. Photos are
 * downscaled to fit before they touch a canvas. The upload path compresses to
 * 1920 px on the longest side afterwards, so nothing visible is lost.
 */
const MAX_EDGE = 4096;
const MAX_PIXELS = 16_000_000;

/** Upper bound for the whole decode → detect → blur → encode pipeline. */
const BLUR_TIMEOUT_MS = 60_000;

/** Size to draw a `width` × `height` image at so it fits the canvas limits. */
function fitToCanvasLimits(width, height) {
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height), Math.sqrt(MAX_PIXELS / (width * height)));
  return {
    width: Math.max(1, Math.floor(width * scale)),
    height: Math.max(1, Math.floor(height * scale))
  };
}

/** Decode the file into a bitmap that is safe to draw on a canvas. */
async function decodeWithinLimits(file) {
  const full = await createImageBitmap(file);
  const target = fitToCanvasLimits(full.width, full.height);
  if (target.width === full.width && target.height === full.height) {
    return full;
  }
  try {
    return await createImageBitmap(full, {
      resizeWidth: target.width,
      resizeHeight: target.height,
      resizeQuality: "high"
    });
  } finally {
    full.close();
  }
}

/**
 * Detect faces in an image and pixelate them.
 * Uses MediaPipe BlazeFace (WASM, works in all browsers including Safari).
 * Model is lazy-loaded on first use (~200KB).
 * Returns the original file if no faces are detected. Fails closed: if the
 * detector or the blurring fails (or takes longer than `BLUR_TIMEOUT_MS`), this
 * throws (with a Swedish message suitable for display) rather than handing back
 * an unblurred photo to be uploaded.
 */
export async function blurFaces(file) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      console.error("Face blur timed out");
      reject(new Error(FACE_BLUR_ERROR_MESSAGE));
    }, BLUR_TIMEOUT_MS);
  });
  try {
    return await Promise.race([blurFacesUnbounded(file), timeout]);
  } finally {
    clearTimeout(timer);
  }
}
async function blurFacesUnbounded(file) {
  try {
    const fd = await getDetector();
    const bitmap = await decodeWithinLimits(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    const result = fd.detect(canvas);
    if (result.detections.length === 0) return file;

    // Pixelate each detected face
    for (const detection of result.detections) {
      const box = detection.boundingBox;
      if (!box) continue;
      const pad = 0.25;
      const bx = Math.max(0, box.originX - box.width * pad);
      const by = Math.max(0, box.originY - box.height * pad);
      const bw = Math.min(canvas.width - bx, box.width * (1 + pad * 2));
      const bh = Math.min(canvas.height - by, box.height * (1 + pad * 2));

      // Pixelation: extract region → scale down tiny → scale back up
      const faceData = ctx.getImageData(bx, by, bw, bh);
      const tmp = document.createElement("canvas");
      tmp.width = bw;
      tmp.height = bh;
      tmp.getContext("2d").putImageData(faceData, 0, 0);
      const small = document.createElement("canvas");
      const scale = 0.04;
      small.width = Math.max(1, Math.floor(bw * scale));
      small.height = Math.max(1, Math.floor(bh * scale));
      small.getContext("2d").drawImage(tmp, 0, 0, small.width, small.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "low";
      ctx.drawImage(small, 0, 0, small.width, small.height, bx, by, bw, bh);
    }
    const blob = await new Promise((resolve, reject) => canvas.toBlob(b => b ? resolve(b) : reject(new Error("toBlob returned null")), "image/webp", 0.9));
    return new File([blob], file.name, {
      type: "image/webp"
    });
  } catch (error) {
    console.error("Face blur failed", error);
    throw new Error(FACE_BLUR_ERROR_MESSAGE);
  }
}